import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { PageHeader } from './PageHeader';
import { HOW_IT_WORKS_STEPS, getEmotion } from '../data/cbtContent';
import './JournalListPage.css';

interface JournalEntry {
  id: string;
  emotion: string;
  intensity: number;
  body_feelings: string | null;
  situation: string;
  automatic_thought: string;
  distortions: string[];
  alternative_thought: string | null;
  new_intensity: number | null;
  created_at: string;
}

export const JournalListPage: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getJournalEntries();
      setEntries(response.data ?? []);
    } catch (e) {
      console.error('Failed to load journal entries', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso: string): string => {
    const date = new Date(iso);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    if (isToday) return 'Сегодня, ' + date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="cbt-journal-page">
      <PageHeader title="КПТ-дневник" />

      <div className="cbt-banner">
        <div className="cbt-banner-emoji">🌿</div>
        <div className="cbt-banner-body">
          <h3 className="cbt-banner-title">Эмоции под рукой</h3>
          <p className="cbt-banner-text">
            Фиксируй сильные эмоции и разбирай их методом когнитивно-поведенческой терапии.
            Чувствуешь, что нужна поддержка? <button className="cbt-banner-link" onClick={() => navigate('/experts')}>Обратиться к эксперту</button>
          </p>
        </div>
      </div>

      <button className="cbt-cta-primary" onClick={() => navigate('/journal/add')}>
        + Новая запись
      </button>

      <section className="cbt-how-it-works">
        <h2 className="cbt-section-title">Как это работает</h2>
        <div className="cbt-steps-grid">
          {HOW_IT_WORKS_STEPS.map((step, idx) => (
            <div className="cbt-step-card" key={idx}>
              <div className="cbt-step-emoji">{step.emoji}</div>
              <div className="cbt-step-title">{step.title}</div>
              <div className="cbt-step-desc">{step.description}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="cbt-entries">
        <h2 className="cbt-section-title">Прошлые записи</h2>

        {loading && <div className="cbt-empty">Загружаем…</div>}

        {!loading && entries.length === 0 && (
          <div className="cbt-empty">
            <div className="cbt-empty-emoji">📓</div>
            <p>Пока записей нет. Начни с первой — это займёт пару минут.</p>
          </div>
        )}

        {!loading && entries.length > 0 && (
          <div className="cbt-entries-list">
            {entries.map((entry) => {
              const emotion = getEmotion(entry.emotion);
              const change = entry.new_intensity !== null
                ? entry.intensity - entry.new_intensity
                : null;
              return (
                <button
                  key={entry.id}
                  className="cbt-entry-card"
                  onClick={() => navigate(`/journal/${entry.id}`)}
                  style={{ borderLeftColor: emotion?.color ?? '#94A3B8' }}
                >
                  <div className="cbt-entry-head">
                    <span className="cbt-entry-emoji">{emotion?.emoji ?? '🙂'}</span>
                    <span className="cbt-entry-emotion-label">{emotion?.label ?? entry.emotion}</span>
                    <span className="cbt-entry-date">{formatDate(entry.created_at)}</span>
                  </div>
                  <div className="cbt-entry-situation">{entry.situation}</div>
                  <div className="cbt-entry-stats">
                    <span>Интенсивность: <b>{entry.intensity}</b>{entry.new_intensity !== null && <> → <b>{entry.new_intensity}</b></>}</span>
                    {change !== null && change > 0 && <span className="cbt-entry-change cbt-down">↓ −{change}</span>}
                    {change !== null && change < 0 && <span className="cbt-entry-change cbt-up">↑ +{-change}</span>}
                    <span className="cbt-entry-distortions">Искажений: <b>{entry.distortions?.length ?? 0}</b></span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};
